import { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import { Button } from '../Button';
import GlobalSpinner from '../globalsSpinner/GlobalSpinner'
import { RightModal, SmallModal } from '../sideModal/Modal';
import { Guid } from '../../../api/baseApi';
import { FormInput, FormRow, FormRowHorizontal, FormRowWithColumns } from '../formRow/FormRow';
import SvgSelector from '../svgSelector/SvgSelector';
import { WeekDays } from '../../../api/priorityListApi';
import FormRange from '../formRow/FormRange';
import FormCheckbox from '../formRow/FormCheckbox';
import IconButton from '../iconButton/IconButton';
import { formatDate, formatTimeOnly } from '../../../services/dateService';
import { VolumeProfile, VolumeProfileRequest, createVolumeProfile, deleteVolumeProfile, getVolumeProfiles, updateVolumeProfile } from '../../../api/playersApi';

type ModalAction = "create" | "edit" | "nothing";

let weekDaysStart: WeekDays = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
}

const VolumeProfiles:FC = () => {
    let [isLoading, setIsLoading] = useState(false);

    let [volumeProfiles, setVolumeProfiles] = useState<VolumeProfile[]>([]);

    let [modalAction, setModalAction] = useState<ModalAction>("nothing");
    let [volumeProfileIdForAction, setVolumeProfileIdForAction] = useState<Guid>('');

    let [volumeProfileName, setVolumeProfileName] = useState<string>("");
    let [isVolumeProfileError, setIsVolumeProfileNameError] = useState<boolean>(false);
    let [volumeProfileStartDate, setVolumeProfileStartDate] = useState('');
    let [isVolumeProfileStartDateError, setIsVolumeProfileStartDateError] = useState(false);
    let [volumeProfileEndDate, setVolumeProfileEndDate] = useState<string | null>('');
    let [isVolumeProfileEndDateError, setIsVolumeProfileEndDateError] = useState(false);
    let [volumeProfileStartTime, setVolumeProfileStartTime] = useState('');
    let [isVolumeProfileStartTimeError, setIsVolumeProfileStartTimeError] = useState(false);
    let [volumeProfileEndTime, setVolumeProfileEndTime] = useState('');
    let [isVolumeProfileEndTimeError, setIsVolumeProfileEndTimeError] = useState(false);


    let [volumeProfileWeekDays, setVolumeProfileWeekDays] = useState<WeekDays>(weekDaysStart);

    let [ volume, setVolume ] = useState<number>(0);

    let [ isUsePrioritization, setIsUsePrioritization ] = useState(false);

    let [isShowDeleteVolumeProfileModal, setIsShowDeleteVolumeProfileModal] = useState(false);

    let {id : playerId} = useParams<{id:string}>();

    useEffect(()=>{
        async function fetchData() {
            setIsLoading(true);
            await Promise.all([updateVolumeProfiles()]);
            setIsLoading(false);
        }

        fetchData()
    },[]);

    const updateVolumeProfiles = async () => {
        let response = await getVolumeProfiles(playerId);
        if( response.type === "succes") {
            setVolumeProfiles(response.data.items);
        }
    }

    const showVolumeModalModal = (action: ModalAction, volumeProfile?:VolumeProfile) => {
        setModalAction(action);
        if(volumeProfile){
            let volumeEndDate = !!volumeProfile.endDate ? volumeProfile.endDate.slice(0,10) : null
            setVolumeProfileIdForAction(volumeProfile.id);
            setVolumeProfileName(volumeProfile.name);
            setVolumeProfileStartDate(volumeProfile.startDate.slice(0,10));
            setVolumeProfileEndDate(volumeEndDate);
            setVolumeProfileStartTime(volumeProfile.startTime);
            setVolumeProfileEndTime(volumeProfile.endTime);
            setVolumeProfileWeekDays(
                {
                    monday: volumeProfile.monday,
                    tuesday: volumeProfile.tuesday,
                    wednesday: volumeProfile.wednesday,
                    thursday: volumeProfile.thursday,
                    friday: volumeProfile.friday,
                    saturday: volumeProfile.saturday,
                    sunday: volumeProfile.sunday
                }
            );
            setVolume(volumeProfile.volume);
            setIsUsePrioritization(volumeProfile.isPriority);
        }
    }

    const hideVolumeModal = () => {
        setModalAction("nothing");
        setVolumeProfileIdForAction("");
        setVolumeProfileName("");
        setIsVolumeProfileNameError(false)
        setVolumeProfileStartDate("");
        setIsVolumeProfileStartDateError(false);
        setVolumeProfileEndDate("");
        setIsVolumeProfileEndDateError(false);
        setVolumeProfileStartTime("");
        setIsVolumeProfileStartTimeError(false)
        setVolumeProfileEndTime("");
        setIsVolumeProfileEndTimeError(false);
        setVolumeProfileWeekDays(weekDaysStart);
        setVolume(0);
        setIsUsePrioritization(false);
    }

    const saveVolumeProfile = async () => {
        let isError = false;
        if(volumeProfileName === ""){
            setIsVolumeProfileNameError(true); isError = true;
        }

        if(volumeProfileStartDate === ""){
            setIsVolumeProfileStartDateError(true); isError = true;
        }

        if(volumeProfileStartTime === ""){
            setIsVolumeProfileStartTimeError(true); isError = true;
        }

        if(volumeProfileEndTime === ""){
            setIsVolumeProfileEndTimeError(true); isError = true;
        }

        if(new Date(!!volumeProfileEndDate ? volumeProfileEndDate : '') < new Date(volumeProfileStartDate)){
            setIsVolumeProfileEndDateError(true); isError = true;
        }
        
        if(isError) return;

        setIsLoading(true);
        let request : VolumeProfileRequest = {
            name: volumeProfileName,
            startDate: volumeProfileStartDate,
            endDate: volumeProfileEndDate,
            startTime: volumeProfileStartTime,
            endTime: volumeProfileEndTime,
            monday: volumeProfileWeekDays.monday,
            tuesday: volumeProfileWeekDays.tuesday,
            wednesday: volumeProfileWeekDays.wednesday,
            thursday: volumeProfileWeekDays.thursday,
            friday: volumeProfileWeekDays.friday,
            saturday: volumeProfileWeekDays.saturday,
            sunday: volumeProfileWeekDays.sunday,
            volume: volume,
            isPriority: isUsePrioritization,
        }

        if(!!!volumeProfileIdForAction){
            await createVolumeProfile(playerId, request)
        }
        else{
            await updateVolumeProfile(playerId, volumeProfileIdForAction, request);
        }

        await updateVolumeProfiles();
        hideVolumeModal();
        setIsLoading(false);
    }

    const renderWeekDaysInModal = (volumeProfileWeekDays: WeekDays) => {
        let d = [];
        for (let day in volumeProfileWeekDays) {
            d.push({
                day: day as keyof WeekDays,
                firstThreeChar: day.slice(0,3),
                isActive: volumeProfileWeekDays[`${day as keyof WeekDays}`]
            })
        }

        return d.map( (it,i) => {
            return <span key={i} className="week-days" onClick={() => changeVolumeProfileDayActivity(it.day, it.isActive)}>
                <SvgSelector title={`${it.isActive ? 'ok' : 'cross'}`} />&nbsp; {it.firstThreeChar} &nbsp;
            </span>
        }) 
    }

    const changeVolumeProfileDayActivity = (day: keyof WeekDays, isActive: boolean) => {
        switch(day){
            case "monday" : 
                setVolumeProfileWeekDays({...volumeProfileWeekDays, monday: !isActive});
                break;
            case "tuesday" : 
                setVolumeProfileWeekDays({...volumeProfileWeekDays, tuesday: !isActive});
                break;
            case "wednesday" : 
                setVolumeProfileWeekDays({...volumeProfileWeekDays, wednesday: !isActive});
                break;
            case "thursday" : 
                setVolumeProfileWeekDays({...volumeProfileWeekDays, thursday: !isActive});
                break;
            case "friday" : 
                setVolumeProfileWeekDays({...volumeProfileWeekDays, friday: !isActive});
                break;
            case "saturday" : 
                setVolumeProfileWeekDays({...volumeProfileWeekDays, saturday: !isActive});
                break;
            case "sunday" : 
                setVolumeProfileWeekDays({...volumeProfileWeekDays, sunday: !isActive});
                break;
        }
    }

    const showDeleteVolumeProfileModal = (id: Guid) => {
        setIsShowDeleteVolumeProfileModal(true);
        setVolumeProfileIdForAction(id);
    }

    const hideDeleteVolumeProfileModal = () => {
        setIsShowDeleteVolumeProfileModal(false);
        setVolumeProfileIdForAction("");
    }

    const removeVolumeProfile = async () => {
        setIsLoading(true);
        await deleteVolumeProfile(playerId, volumeProfileIdForAction);
        await updateVolumeProfiles();
        hideDeleteVolumeProfileModal()
        setIsLoading(false);
    }

    const renderVolumeProfile = (volumeProfile: VolumeProfile) => {
        return <tr key={volumeProfile.id}>
                    <td>{volumeProfile.name}</td>
                    <td>{formatDate(volumeProfile.startDate)}</td>
                    <td>{!!volumeProfile.endDate && formatDate(volumeProfile.endDate)}</td>
                    <td>{formatTimeOnly(volumeProfile.startTime)}</td>
                    <td>{formatTimeOnly(volumeProfile.endTime)}</td>
                    <td>
                        <span className="table-week-days">
                            <span className="week-day">
                                <span>M</span>
                                <span>{volumeProfile.monday ? <SvgSelector title={ "ok" } /> : <SvgSelector title={ "cross" } />}</span>
                            </span>
                            <span className="week-day">
                                <span>T</span>
                                <span>{volumeProfile.tuesday ? <SvgSelector title={ "ok" } /> : <SvgSelector title={ "cross" } />}</span>
                            </span>
                            <span className="week-day">
                                <span>W</span>
                                <span>{volumeProfile.wednesday ? <SvgSelector title={ "ok" } /> : <SvgSelector title={ "cross" } />}</span>
                            </span>
                            <span className="week-day">
                                <span>Th</span>
                                <span>{volumeProfile.thursday ? <SvgSelector title={ "ok" } /> : <SvgSelector title={ "cross" } />}</span>
                            </span>
                            <span className="week-day">
                                <span>F</span>
                                <span>{volumeProfile.friday ? <SvgSelector title={ "ok" } /> : <SvgSelector title={ "cross" } />}</span>
                            </span>
                            <span className="week-day">
                                <span>S</span>
                                <span>{volumeProfile.saturday ? <SvgSelector title={ "ok" } /> : <SvgSelector title={ "cross" } />}</span>
                            </span>
                            <span className="week-day">
                                <span>Su</span>
                                <span>{volumeProfile.sunday ? <SvgSelector title={ "ok" } /> : <SvgSelector title={ "cross" } />}</span>
                            </span>
                        </span>
                    </td>
                    <td>
                        {volumeProfile.volume}%
                    </td>
                    
                    <td>
						<IconButton tooltipText="Delete" type="delete" onClick={() => {showDeleteVolumeProfileModal(volumeProfile.id)}} />
                        <IconButton tooltipText="Edit" type="edit" onClick={() => {showVolumeModalModal('edit', volumeProfile)}} />
                    </td>
                </tr>
    }


    return <>
        <div className="d-flex justify-content-between align-items-center mb-20 w-100">
            <h2>Priority Profiles</h2>
            <Button 
                color="green"  
                label="Add Volume Profile" 
                onClick={() => showVolumeModalModal("create")} 
            />
        </div>  

        {volumeProfiles.length ? <table className="table">
            <thead>
                <tr>
                    <td>Name</td>
                    <td>Start Date</td>
                    <td>End Date</td>
                    <td>Start Time</td>
                    <td>End Time</td>
                    <td>Days of Week</td>
                    <td>Volume</td>
                    <td></td>
                </tr>
            </thead>
            <tbody>
                {volumeProfiles.filter(it => it.isPriority === true).map(renderVolumeProfile)}
            </tbody>
        </table> : <p>List of profiles is empty</p>}

        <div className="d-flex justify-content-between align-items-center pt-30 mb-20 w-100">
            <h2>Repeat Profiles</h2>
        </div>  

        {volumeProfiles.length ? <table className="table">
            <thead>
                <tr>
                    <td>Name</td>
                    <td>Start Date</td>
                    <td>End Date</td>
                    <td>Start Time</td>
                    <td>End Time</td>
                    <td>Days of Week</td>
                    <td>Volume</td>
                    <td></td>
                </tr>
            </thead>
            <tbody>
                {volumeProfiles.filter(it => it.isPriority === false).map(renderVolumeProfile)}
            </tbody>
        </table> : <p>List of profiles is empty</p>}

        <RightModal title={modalAction === "edit" ? "Edit Volume Profile" : "Add Volume Profile"} show={modalAction !== "nothing"} onRequestClose={hideVolumeModal}>
            
            <FormRow>
                <FormInput 
                    errorMessage = "Please enter name"
                    id = "volume_profile_name_input"
                    isError = {isVolumeProfileError}
                    title = "Volume Profile Name"
                    onChangeValue = {(v) => {setVolumeProfileName(v); setIsVolumeProfileNameError(false)}}
                    value = {volumeProfileName}
                />
            </FormRow>
            <FormRowWithColumns>
                <FormRow>
                    <FormInput
                        errorMessage="Please select Start Date"
                        id="r_stream_start_date"
                        isError={isVolumeProfileStartDateError}
                        type="date"
                        onChangeValue={(v) => {setIsVolumeProfileStartDateError(false); setVolumeProfileStartDate(v)}}
                        value={volumeProfileStartDate}
                        title="Start Date"
                    />                         
                </FormRow>
                <FormRow>
                    <FormInput
                        errorMessage="End Date should be later than Start Date"
                        id="r_stream_end_date"
                        isError={isVolumeProfileEndDateError}
                        type="date"
                        onChangeValue={(v) => {
                            setIsVolumeProfileEndDateError(false); 
                            setVolumeProfileEndDate(!!v ? v : null)
                        }}
                        value={!!volumeProfileEndDate ? volumeProfileEndDate : ''}
                        title="End Date"
                    />  
                </FormRow>
            </FormRowWithColumns>
            <FormRowWithColumns>
                <FormRow>
                        <FormInput
                        errorMessage="Please select Start Time"
                        id="r_stream_start_time"
                        isError={isVolumeProfileStartTimeError}
                        type="time"
                        onChangeValue={(v) => {setIsVolumeProfileStartTimeError(false); setVolumeProfileStartTime(v)}}
                        value={volumeProfileStartTime}
                        title="Start Time"
                        />  
                
                </FormRow>
                <FormRow>
                        <FormInput
                        errorMessage="Please select End Time"
                        id="r_stream_end_time"
                        isError={isVolumeProfileEndTimeError}
                        type="time"
                        onChangeValue={(v) => {setIsVolumeProfileEndTimeError(false); setVolumeProfileEndTime(v)}}
                        value={volumeProfileEndTime}
                        title="End Time"
                        />  
                    
                </FormRow>
            </FormRowWithColumns>
            <FormRowHorizontal>
                {renderWeekDaysInModal(volumeProfileWeekDays)}
            </FormRowHorizontal>

            <FormRow>
                <FormRange
                    id="volume-range"
                    title="Volume Volume"
                    min={0}
                    max={100}
                    step={1}
                    value={volume}
                    changeValue={setVolume}
                />
            </FormRow>

            <FormRowHorizontal>
                <div className="status-container">
                    <span className="status-block">
                        {isUsePrioritization ? <span className="current-status online">Use Prioritization</span> : <span className="current-status offline">Use Prioritization</span>}
                    </span>
                    <FormCheckbox id="use-prioritization" checked={isUsePrioritization} onChange={setIsUsePrioritization} />
                </div>
            </FormRowHorizontal>
            
            <div className="modal-btn-group">
                <Button color="green" label="Save" onClick={saveVolumeProfile}/>
                <Button color="gray" label="Cancel" onClick={hideVolumeModal}/>
            </div>
        </RightModal>  

        <SmallModal show={isShowDeleteVolumeProfileModal} onRequestClose={hideDeleteVolumeProfileModal}> 
            <p>That you want to delete Volume Profile?</p>
            <div className="modal-btn-group">
                <Button color="gray" label="Cancel" onClick={hideDeleteVolumeProfileModal}/>
                <Button color="green" label="Yes" onClick={removeVolumeProfile}/>
            </div>
        </SmallModal>

        <GlobalSpinner isShow={isLoading} />
    </>
}

export default VolumeProfiles
